import {currentTheme as currentVizTheme, refreshTheme,} from 'devextreme/viz/themes';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';

// Tema altyapısı (02.10.2026): eski hâli — blue/teal eksikti
// const themes = ['light', 'contrast', 'dark', 'carmine','darkmoon','darkviolet','softblue','greenmist','orange'] as const;
const themes = ['light', 'contrast', 'dark', 'carmine', 'darkmoon', 'darkviolet', 'softblue', 'greenmist', 'orange', 'blue', 'teal'] as const;

/** Koyu zeminli temalar (libs/panel panel-tema.service KOYU_TEMALAR ile aynı). */
const KOYU_TEMALAR: ReadonlySet<string> = new Set(['dark', 'contrast', 'darkmoon', 'darkviolet']);

/**
 * index.html'deki betiğin eklediği tek tema <link>'i. project.json'da tema paketleri inject:false olduğundan
 * açılışta yalnız seçili tema iner; geçişte bu link'in adresi değişir.
 */
const TEMA_LINK_ID = 'dk-tema';

type Theme = (typeof themes)[number];

function getNextTheme(theme?: Theme) {
  return themes[themes.indexOf(theme) + 1] || themes[0];
}

export class ThemeObject {
  value: number | string;

  name: string;

  icon: string;

  mode?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public selectedThemeValue:any;
  public themeMode: 'M' | 'G' = 'G'; // Varsayılan olarak 'M' modu
  public themeSettings: ThemeObject[] = [
    { value: 1, name: 'Gece', icon: 'moon', mode: 'G' },
    { value: 2, name: 'Normal', icon: 'sun', mode: 'G' },
    { value: 3, name: 'Kontrast', icon: 'pinleft', mode: 'G' },
    { value: 4, name: 'Carmine', icon: 'fill', mode: 'G' },
    { value: 5, name: 'Softdark', icon: 'fill', mode: 'G' },
    { value: 6, name: 'Darkmoon', icon: 'fill', mode: 'G' },
    { value: 7, name: 'Darkviolet', icon: 'fill', mode: 'G' },
    { value: 8, name: 'Greenmist', icon: 'fill', mode: 'G' },
    { value: 9, name: 'T-Orange-M', icon: 'fill', mode: 'M' },
    { value: 10, name: 'T-Blue-M', icon: 'fill', mode: 'M' },
    { value: 11, name: 'T-Teal-M', icon: 'fill', mode: 'M' },
  ];
  private storageKey = 'app-theme';
  private themeMarker = 'theme-';

  public currentTheme: Theme =
    window.localStorage[this.storageKey] || getNextTheme();

  // Tema altyapısı (02.10.2026): eski hâli — ters çalışıyordu (Normal'de true)
  // public isDark = new BehaviorSubject<boolean>(this.currentTheme === 'light');
  public isDark = new BehaviorSubject<boolean>(KOYU_TEMALAR.has(this.currentTheme));

  private themeSubject: BehaviorSubject<string> = new BehaviorSubject<string>('light');
  public theme$: Observable<string> = this.themeSubject.asObservable();

  private getThemeStyleSheets() {
    return Array.from(document.styleSheets).filter((styleSheet) =>
      styleSheet?.href?.includes(this.themeMarker)
    );
  }

  getThemeSettings(): ThemeObject[] {
    return this.themeSettings;
  }

  /** 'http://.../theme-darkmoon.css' → 'darkmoon' (hash'li adda da çalışır). */
  private temaAdi(href: string | null | undefined): string | undefined {
    return /theme-([a-z]+)(?:\.[0-9a-f]+)?\.css/.exec(href ?? '')?.[1];
  }

  setAppTheme(theme = this.currentTheme) {
    // Tema altyapısı (02.10.2026): eski hâli — 'theme-dark' öneki theme-darkmoon/darkviolet'i de açık bırakıyordu
    // this.getThemeStyleSheets().forEach((styleSheet) => {
    //   styleSheet.disabled = !styleSheet?.href?.includes(
    //     `${this.themeMarker}${theme}`
    //   );
    // });
    const temaLink = document.getElementById(TEMA_LINK_ID) as HTMLLinkElement | null;
    if (temaLink) {
      // Yalnız seçili tema yüklü: dosya adresini değiştir
      const hedef = `${this.themeMarker}${theme}.css`;
      if (this.temaAdi(temaLink.getAttribute('href')) !== theme) temaLink.setAttribute('href', hedef);
    } else {
      // Tüm temalar yüklü (eski derleme): yalnız seçili açık, tam ad eşleşmesiyle
      this.getThemeStyleSheets().forEach((styleSheet) => {
        styleSheet.disabled = this.temaAdi(styleSheet?.href) !== theme;
      });
    }

    this.currentTheme = theme;
    // Tema altyapısı (02.10.2026): eski hâli — this.isDark.next(this.currentTheme === 'light');
    this.isDark.next(KOYU_TEMALAR.has(this.currentTheme));
    this.themeSubject.next(this.currentTheme);

    currentVizTheme(

      currentVizTheme().replace(/\.[a-z]+\.compact$/, `.${theme}.compact`)
    );
    this.themeSettings.forEach((item:any) => {
      if(item.name === this.currentTheme) this.selectedThemeValue = this.currentTheme
    })
    refreshTheme();
  }

  getCurrentTheme() {
    return this.currentTheme;
  }

  getCurrentThemeIcon() {
    const currentTheme =
      this.currentTheme === 'light'
        ? 'sun'
        : this.currentTheme === 'dark'
          ? 'moon'
          : this.currentTheme === 'contrast'
            ? 'pinleft'
            : 'fill';
    return currentTheme;
  }
  getCurrentThemeName() {
    const themeMap: { [key: string]: string } = {
      light: 'Normal',
      dark: 'Gece',
      contrast: 'Kontrast',
      carmine: 'Carmine',
      softblue: 'Softdark',
      greenmist: 'Greenmist',
      darkviolet: 'Darkviolet',
      darkmoon: 'Darkmoon',
      orange: 'T-Orange-M',
      blue: 'T-Blue-M',
      teal: 'T-Teal-M'
    };

    return themeMap[this.currentTheme] || 'Seçiniz...';
  }
  switchTheme(theme: any) {
    const newTheme = theme;
    this.setAppTheme(newTheme);
    window.localStorage[this.storageKey] = newTheme;
  }
}
